import React from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { useTheme } from '../styles/ThemeProvider';
import { LLMJudgeMetrics } from '../types';

// ─── Styled Components ────────────────────────────────────────────────────────

const Ribbon = styled.div<{ borderColor: string; bgColor: string }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  padding: 10px 16px;
  margin-top: 10px;
  background: ${props => props.bgColor};
  border: 1px solid ${props => props.borderColor};
  border-radius: 8px;
  animation: fadeIn 0.4s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const RibbonLabel = styled.span<{ color: string }>`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`;

const MetricGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MetricName = styled.span<{ color: string }>`
  font-size: 11px;
  color: ${props => props.color};
  white-space: nowrap;
`;

const MetricScore = styled.span<{ score: number }>`
  font-size: 12px;
  font-weight: 700;
  color: ${props => scoreColor(props.score)};
  min-width: 30px;
  text-align: right;
`;

const ScoreBar = styled.div<{ score: number; width?: string }>`
  height: 6px;
  width: ${props => props.width || '40px'};
  background: #e1e8ed;
  border-radius: 3px;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => props.score}%;
    background: ${props => scoreColor(props.score)};
    border-radius: 3px;
    transition: width 0.6s ease;
  }
`;

const Divider = styled.div<{ color: string }>`
  width: 1px;
  height: 20px;
  background: ${props => props.color};
  flex-shrink: 0;
`;

const LatencyBadge = styled.span<{ color: string }>`
  font-size: 11px;
  color: ${props => props.color};
  white-space: nowrap;
`;

const SourceBadge = styled.span<{ bgColor: string; textColor: string }>`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 8px;
  background: ${props => props.bgColor};
  color: ${props => props.textColor};
  font-weight: 500;
`;

const ConcernsRow = styled.div<{ color: string }>`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const ConcernTag = styled.span<{ borderColor: string; textColor: string }>`
  font-size: 11px;
  padding: 2px 8px;
  border: 1px solid ${props => props.borderColor};
  border-radius: 10px;
  color: ${props => props.textColor};
`;

const LoadingRibbon = styled.div<{ borderColor: string; bgColor: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  margin-top: 10px;
  background: ${props => props.bgColor};
  border: 1px solid ${props => props.borderColor};
  border-radius: 8px;
`;

const Spinner = styled.div<{ color: string }>`
  width: 14px;
  height: 14px;
  border: 2px solid ${props => props.color}30;
  border-top-color: ${props => props.color};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin { to { transform: rotate(360deg); } }
`;

// ─── Helper ───────────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 85) return '#00b894';
  if (score >= 70) return '#52bd94';
  if (score >= 55) return '#ff9e1b';
  return '#e74c3c';
}

function formatLatency(ms: number): string {
  if (ms === 0) return '';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface LLMJudgeRibbonProps {
  metrics: LLMJudgeMetrics | null;
  loading?: boolean;
  label?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const LLMJudgeRibbon: React.FC<LLMJudgeRibbonProps> = ({
  metrics,
  loading = false,
  label = 'AI Quality',
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <LoadingRibbon borderColor={theme.colors.border} bgColor={theme.colors.bgLight}>
        <Spinner color={theme.colors.accentTeal} />
        <span style={{ fontSize: 12, color: theme.colors.textMuted }}>
          Evaluating quality with LLM-as-a-Judge...
        </span>
      </LoadingRibbon>
    );
  }

  if (!metrics) return null;

  const sourceLabel = {
    'llm-judge': 'LLM Judge',
    'llm-judge-consensus': metrics.judgeRuns ? `Consensus (${metrics.judgeRuns}x)` : 'Consensus',
    'heuristic': 'Heuristic',
    'validation-metrics': 'Rule-Based',
  }[metrics.source] ?? 'Judge';

  const tealAlpha30 = `${theme.colors.accentTeal}30`;
  const tealAlpha05 = `${theme.colors.accentTeal}05`;
  const warningAlpha50 = `${theme.colors.warning}50`;

  const sourceBg = (metrics.source === 'llm-judge' || metrics.source === 'llm-judge-consensus')
    ? `${theme.colors.accentTeal}15`
    : `${theme.colors.textMuted}15`;
  const sourceTxt = (metrics.source === 'llm-judge' || metrics.source === 'llm-judge-consensus')
    ? theme.colors.accentTeal
    : theme.colors.textMuted;

  const dims: { key: keyof LLMJudgeMetrics; name: string }[] = [
    { key: 'accuracy',     name: 'Accuracy' },
    { key: 'completeness', name: 'Coverage' },
    { key: 'consistency',  name: 'Consistency' },
    { key: 'overall',      name: 'Overall' },
  ];

  return (
    <Ribbon borderColor={tealAlpha30} bgColor={tealAlpha05}>
      <RibbonLabel color={theme.colors.accentTeal}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
        {label}
      </RibbonLabel>

      <SourceBadge bgColor={sourceBg} textColor={sourceTxt}>
        {sourceLabel}
      </SourceBadge>

      <Divider color={theme.colors.border} />

      {dims.map(({ key, name }) => {
        const score = metrics[key] as number;
        return (
          <MetricGroup key={key}>
            <MetricName color={theme.colors.textMuted}>{name}</MetricName>
            <ScoreBar score={score} />
            <MetricScore score={score}>{score}</MetricScore>
          </MetricGroup>
        );
      })}

      {metrics.latencyMs > 0 && (
        <>
          <Divider color={theme.colors.border} />
          <LatencyBadge color={theme.colors.textMuted}>
            ⏱ {formatLatency(metrics.latencyMs)}
          </LatencyBadge>
        </>
      )}

      {metrics.summary && (
        <ConcernsRow color={theme.colors.textMuted}>
          <span style={{ fontSize: 11, color: theme.colors.textSecondary, fontStyle: 'italic' }}>
            {metrics.summary}
          </span>
        </ConcernsRow>
      )}

      {metrics.concerns && metrics.concerns.length > 0 && (
        <ConcernsRow color={theme.colors.textMuted}>
          {metrics.concerns.map((c) => (
            <ConcernTag key={c} borderColor={warningAlpha50} textColor={theme.colors.warning}>
              ⚠ {c}
            </ConcernTag>
          ))}
        </ConcernsRow>
      )}
    </Ribbon>
  );
};
